import {
  getState,
  setFromRemote,
  updateTitle,
  updateCollection,
  updateDifficulty,
  updateDescription,
  updateTimeLimit,
  updateShuffleAnswers,
  updateQuestion,
  reorderQuestions,
  addQuestion,
  removeQuestion,
  subscribeToChanges,
  addAnswer,
  updateAnswer,
  removeAnswer,
} from "../stores/test_store";
import type { Question, AnswerOption, TestState } from "../stores/test_store";
import { AutosaveService } from "../services/autosave";
import type { AutosaveOptions } from "../services/autosave";
import Sortable from "sortablejs";
import { DirectUpload } from "@rails/activestorage";
import { CascadingCollectionsController } from "../components/cascading_collections";
import { queryOptional } from "../utils/dom";

type ServerQuestion = {
  uuid: string;
  content?: string;
  position: number;
  question_type?: Question["question_type"];
  answer_options?: AnswerOption[];
  image_url?: string;
};

type ServerResponse = {
  title: string;
  collection_id?: number;
  difficulty?: string;
  description?: string;
  time_limit_seconds?: number;
  shuffle_answers?: boolean;
  questions: ServerQuestion[];
};

export class TestEditor {
  private testId: string;
  private serverUpdatedAt: number;
  private sortable: Sortable | null = null;
  private rootElement: HTMLElement;
  private titleInput: HTMLInputElement | null = null;
  private difficultySelect: HTMLSelectElement | null = null;
  private cascadingCollections: CascadingCollectionsController | null = null;
  private descriptionTextarea: HTMLTextAreaElement | null = null;
  private timeLimitInput: HTMLInputElement | null = null;
  private shuffleAnswersToggle: HTMLElement | null = null;
  private questionsContainer: HTMLElement | null = null;
  private toolboxElement: HTMLElement | null = null;
  private propertiesElement: HTMLElement | null = null;
  private canvasElement: HTMLElement | null = null;
  private canvasBtn: HTMLElement | null = null;
  private toolboxBtn: HTMLElement | null = null;
  private propertiesBtn: HTMLElement | null = null;
  private unsubscribe: (() => void) | null = null;
  private autosave = new AutosaveService(5000);
  private saveStatus: HTMLElement | null = null;
  private loadingOverlay: HTMLElement | null = null;
  private initPromise: Promise<void>;
  private isSyncingFromServer = false;

  constructor(rootElement: HTMLElement) {
    this.rootElement = rootElement;
    this.testId = rootElement.dataset.testId ?? "";
    this.serverUpdatedAt = parseInt(rootElement.dataset.serverUpdatedAt ?? "0");

    this.cacheElements();
    this.initCascadingCollections();
    this.bindEvents();
    const collectionsReady =
      this.cascadingCollections?.ready() ?? Promise.resolve();
    this.initPromise = Promise.all([
      this.resolveConflicts(),
      collectionsReady,
    ]).then(() => {
      this.subscribeToStore();
      this.hideLoadingOverlay();
    });
  }

  ready(): Promise<void> {
    return this.initPromise;
  }

  private hideLoadingOverlay(): void {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.add("opacity-0");
      setTimeout(() => {
        this.loadingOverlay?.remove();
      }, 300);
    }
  }

  private initCascadingCollections(): void {
    const container = document.getElementById("collection-selector-container");
    if (container) {
      this.cascadingCollections = new CascadingCollectionsController(container);
      this.cascadingCollections.setOnChange((collectionId: number | null) => {
        if (collectionId !== null) {
          updateCollection(this.testId, collectionId);
        }
      });
    }
  }

  private cacheElements(): void {
    this.titleInput = this.rootElement.querySelector("#test-title");
    this.difficultySelect = this.rootElement.querySelector(
      'select[name="test[difficulty]"]',
    );
    this.descriptionTextarea = this.rootElement.querySelector(
      'textarea[name="test[description]"]',
    );
    this.timeLimitInput = this.rootElement.querySelector("#test-time-limit");
    this.shuffleAnswersToggle = this.rootElement.querySelector(
      "#shuffle-answers-toggle",
    );
    this.questionsContainer = this.rootElement.querySelector(
      "#questions-container",
    );
    this.toolboxElement = this.rootElement.querySelector(
      '[data-test-editor-layout-target="toolbox"]',
    );
    this.propertiesElement = this.rootElement.querySelector(
      '[data-test-editor-layout-target="properties"]',
    );
    this.canvasElement = this.rootElement.querySelector(
      '[data-test-editor-layout-target="canvas"]',
    );
    this.canvasBtn = this.rootElement.querySelector(
      '[data-test-editor-layout-target="canvasBtn"]',
    );
    this.toolboxBtn = this.rootElement.querySelector(
      '[data-test-editor-layout-target="toolboxBtn"]',
    );
    this.propertiesBtn = this.rootElement.querySelector(
      '[data-test-editor-layout-target="propertiesBtn"]',
    );
    this.saveStatus = this.rootElement.querySelector("#save-status");
    this.loadingOverlay = this.rootElement.querySelector(
      "#editor-loading-overlay",
    );
  }

  private subscribeToStore(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }

    const unsubscribeChange = subscribeToChanges(this.testId, () => {
      const state = getState(this.testId);
      if (state) {
        this.renderTitle(state.title);
        this.renderTestProperties(state);
        this.renderQuestions(state.questions);
        this.scheduleAutosave(state);
      }
    });

    this.unsubscribe = () => {
      unsubscribeChange();
    };

    const state = getState(this.testId);
    if (state) {
      this.renderTitle(state.title);
      this.renderTestProperties(state);
      this.renderQuestions(state.questions);
    }
  }

  private renderTestProperties(state: TestState): void {
    if (this.difficultySelect && state.difficulty) {
      this.difficultySelect.value = state.difficulty;
    }
    if (
      this.descriptionTextarea &&
      this.descriptionTextarea.value !== (state.description ?? "")
    ) {
      this.descriptionTextarea.value = state.description ?? "";
    }
    if (this.timeLimitInput && typeof state.time_limit_seconds === "number") {
      const minutes = Math.floor(state.time_limit_seconds / 60);
      if (parseInt(this.timeLimitInput.value) !== minutes) {
        this.timeLimitInput.value = minutes.toString();
      }
    }
    if (this.shuffleAnswersToggle) {
      const shuffleAnswers = state.shuffle_answers ?? true;
      const isEnabled = this.shuffleAnswersToggle.dataset.shuffle === "true";
      if (isEnabled !== shuffleAnswers) {
        this.updateShuffleAnswersToggle(shuffleAnswers);
      }
    }
  }

  private updateShuffleAnswersToggle(enabled: boolean): void {
    if (!this.shuffleAnswersToggle) return;

    this.shuffleAnswersToggle.dataset.shuffle = enabled.toString();
    const knob = this.shuffleAnswersToggle.querySelector("span");
    if (enabled) {
      this.shuffleAnswersToggle.classList.remove("bg-slate-700");
      this.shuffleAnswersToggle.classList.add("bg-blue-600");
      knob?.classList.add("translate-x-5");
    } else {
      this.shuffleAnswersToggle.classList.add("bg-slate-700");
      this.shuffleAnswersToggle.classList.remove("bg-blue-600");
      knob?.classList.remove("translate-x-5");
    }
  }

  private formatForServer(state: TestState): AutosaveOptions["data"] | null {
    if (!state) return null;
    const questionsAttributes = state.questions
      .map((q, index) => {
        const answerOptions = (q.answer_options || [])
          .filter((a) => a.content?.trim() || a._destroy)
          .map((a) => {
            const ansRecord: {
              uuid: string;
              content?: string;
              is_correct: boolean;
              _destroy?: boolean;
            } = {
              uuid: a.uuid,
              is_correct: !!a.is_correct,
            };
            const trimmed = a.content?.trim();
            if (trimmed) ansRecord.content = trimmed;
            if (a._destroy) ansRecord._destroy = true;
            return ansRecord;
          });

        const record: {
          uuid: string;
          content?: string;
          position: number;
          question_type: Question["question_type"];
          image?: string;
          answer_options_attributes: typeof answerOptions;
          _destroy?: boolean;
        } = {
          uuid: q.uuid,
          position: index,
          question_type: q.question_type,
          answer_options_attributes: answerOptions,
        };
        if (q.content) record.content = q.content;
        if (q.image) record.image = q.image;
        if (q._destroy) record._destroy = true;
        return record;
      })
      .filter((q) => {
        if (q._destroy) return true;
        if (q.content) return true;
        return false;
      });

    const result: AutosaveOptions["data"] = {
      title: state.title,
      questions_attributes: questionsAttributes,
    };
    if (state.collection_id !== undefined)
      result.collection_id = state.collection_id;
    if (state.difficulty !== undefined) result.difficulty = state.difficulty;
    if (state.description !== undefined) result.description = state.description;
    if (state.time_limit_seconds !== undefined)
      result.time_limit_seconds = state.time_limit_seconds;
    if (state.shuffle_answers !== undefined)
      result.shuffle_answers = state.shuffle_answers;
    return result;
  }

  private scheduleAutosave(state: TestState): void {
    if (this.isSyncingFromServer) return;

    const data = this.formatForServer(state);
    if (!data) return;

    this.updateSaveStatus("saving");
    this.autosave.scheduleSave({
      testId: this.testId,
      data: data,
      onSuccess: async (serverUpdatedAt) => {
        this.serverUpdatedAt = serverUpdatedAt;
        this.updateSaveStatus("saved");

        const freshData = await this.fetchServerDataOnly();
        if (freshData) {
          const localState = getState(this.testId);
          const localEmptyAnswers = new Map<string, AnswerOption[]>();

          localState?.questions.forEach((q) => {
            const emptyAnswers = (q.answer_options || []).filter(
              (a) => !a.content?.trim() && !a._destroy,
            );
            if (emptyAnswers.length > 0) {
              localEmptyAnswers.set(q.uuid, emptyAnswers);
            }
          });

          this.isSyncingFromServer = true;
          setFromRemote(this.testId, {
            title: freshData.title,
            ...(freshData.time_limit_seconds !== undefined
              ? { time_limit_seconds: freshData.time_limit_seconds }
              : {}),
            shuffle_answers: freshData.shuffle_answers ?? true,
            questions: freshData.questions.map((q) => {
              const localEmpty = localEmptyAnswers.get(q.uuid) || [];
              return {
                uuid: q.uuid,
                content: q.content ?? "",
                position: q.position,
                question_type: q.question_type ?? "multiple_choice",
                answer_options: [...(q.answer_options ?? []), ...localEmpty],
                ...(q.image_url ? { image_url: q.image_url } : {}),
              };
            }),
            lastModified: Date.now(),
            ...(freshData.collection_id
              ? { collection_id: freshData.collection_id }
              : {}),
            ...(freshData.difficulty
              ? { difficulty: freshData.difficulty }
              : {}),
            ...(freshData.description
              ? { description: freshData.description }
              : {}),
          });
          this.isSyncingFromServer = false;
        }
      },
      onError: (error) => {
        if (error === 404) {
          this.updateSaveStatus("offline");
          this.showToast(
            "Some questions were deleted - cleaning up...",
            "info",
          );
          this.handle404AndRefresh();
        } else {
          this.updateSaveStatus("offline");
        }
      },
    });
  }

  private handle404AndRefresh(): void {
    const state = getState(this.testId);
    if (state) {
      const destroyedQuestionUuids = state.questions
        .filter((q) => q._destroy)
        .map((q) => q.uuid);
      destroyedQuestionUuids.forEach((uuid) => {
        removeQuestion(this.testId, uuid);
      });
    }
    this.fetchServerData();
    this.showToast("Syncing with server...", "info");
  }

  private updateSaveStatus(status: "saving" | "saved" | "offline"): void {
    if (!this.saveStatus) return;

    const saving = this.saveStatus.querySelector(".status-saving");
    const saved = this.saveStatus.querySelector(".status-saved");
    const offline = this.saveStatus.querySelector(".status-offline");

    saving?.classList.add("hidden");
    saved?.classList.add("hidden");
    offline?.classList.add("hidden");

    switch (status) {
      case "saving":
        saving?.classList.remove("hidden");
        break;
      case "saved":
        saved?.classList.remove("hidden");
        break;
      case "offline":
        offline?.classList.remove("hidden");
        break;
    }
  }

  private async resolveConflicts(): Promise<void> {
    const localState = getState(this.testId);

    if (!localState) {
      await this.fetchServerData();
      return;
    }

    const serverData = await this.fetchServerDataOnly();

    if (!serverData) {
      await this.fetchServerData();
      return;
    }

    const serverQuestionUuids = new Set(
      serverData.questions.map((q) => q.uuid),
    );

    const localContentByUuid = new Map<string, string>();
    localState.questions.forEach((q) => {
      localContentByUuid.set(q.uuid, q.content);
    });

    const mergedQuestions = serverData.questions.map((q) => {
      const localQ = localState.questions.find((lq) => lq.uuid === q.uuid);
      let mergedAnswers = q.answer_options ?? [];

      if (localQ?.answer_options) {
        const serverAnswerUuids = new Set(mergedAnswers.map((a) => a.uuid));
        const localAnswerMap = new Map(
          localQ.answer_options.map((a) => [a.uuid, a]),
        );

        mergedAnswers = mergedAnswers.map((sa) => {
          const localA = localAnswerMap.get(sa.uuid);
          return localA
            ? {
                ...sa,
                content: localA.content,
                is_correct: localA.is_correct,
                ...(localA._destroy ? { _destroy: true } : {}),
              }
            : sa;
        });

        const newLocalAnswers = localQ.answer_options.filter(
          (a) => !serverAnswerUuids.has(a.uuid) && !a._destroy,
        );
        mergedAnswers = [...mergedAnswers, ...newLocalAnswers];
      }

      return {
        uuid: q.uuid,
        content: localContentByUuid.get(q.uuid) ?? q.content ?? "",
        position: localQ ? localQ.position : q.position,
        question_type: q.question_type ?? "multiple_choice",
        answer_options: mergedAnswers,
        ...(localQ?.image_url || q.image_url
          ? { image_url: localQ?.image_url ?? q.image_url }
          : {}),
        ...(localQ?.image ? { image: localQ.image } : {}),
      };
    });

    const localNewQuestions = localState.questions.filter(
      (q) => !serverQuestionUuids.has(q.uuid) && !q._destroy,
    );

    const allQuestions = [...mergedQuestions, ...localNewQuestions];

    this.isSyncingFromServer = true;
    setFromRemote(this.testId, {
      title: localState.title,
      ...(localState.time_limit_seconds !== undefined ||
      serverData.time_limit_seconds !== undefined
        ? {
            time_limit_seconds:
              localState.time_limit_seconds ?? serverData.time_limit_seconds,
          }
        : {}),
      shuffle_answers:
        localState.shuffle_answers ?? serverData.shuffle_answers ?? true,
      questions: allQuestions,
      lastModified: Date.now(),
      ...((localState.collection_id ?? serverData.collection_id)
        ? {
            collection_id: localState.collection_id ?? serverData.collection_id,
          }
        : {}),
      ...((localState.difficulty ?? serverData.difficulty)
        ? { difficulty: localState.difficulty ?? serverData.difficulty }
        : {}),
      ...((localState.description ?? serverData.description)
        ? { description: localState.description ?? serverData.description }
        : {}),
    });
    this.isSyncingFromServer = false;

    this.showToast("Synced with server", "info");
  }

  private async fetchServerDataOnly(): Promise<ServerResponse | null> {
    try {
      const response = await fetch(`/studio/tests/${this.testId}.json`);
      if (response.ok) {
        return await response.json();
      }
    } catch {
      console.error("Failed to fetch server data:");
    }
    return null;
  }

  private async fetchServerData(): Promise<void> {
    try {
      const response = await fetch(`/studio/tests/${this.testId}.json`);
      if (response.ok) {
        const data: ServerResponse = await response.json();
        this.isSyncingFromServer = true;
        setFromRemote(this.testId, {
          title: data.title,
          ...(data.time_limit_seconds !== undefined
            ? { time_limit_seconds: data.time_limit_seconds }
            : {}),
          shuffle_answers: data.shuffle_answers ?? true,
          questions: data.questions.map((q) => ({
            uuid: q.uuid,
            content: q.content ?? "",
            position: q.position,
            question_type: q.question_type ?? "multiple_choice",
            answer_options: q.answer_options ?? [],
            ...(q.image_url ? { image_url: q.image_url } : {}),
          })),
          lastModified: Date.now(),
          ...(data.collection_id ? { collection_id: data.collection_id } : {}),
          ...(data.difficulty ? { difficulty: data.difficulty } : {}),
          ...(data.description ? { description: data.description } : {}),
        });
        this.isSyncingFromServer = false;
      }
    } catch {
      console.error("Failed to fetch server data:");
    }
  }

  private renderQuestionImage(card: HTMLElement, q: Question): void {
    const preview = queryOptional<HTMLImageElement>(
      ".question-image-preview",
      card,
    );
    const placeholder = card.querySelector(".placeholder");

    if (q.image_url && preview) {
      const currentUrl = preview.dataset.currentUrl;
      if (currentUrl !== q.image_url) {
        preview.src = q.image_url;
        preview.dataset.currentUrl = q.image_url;
      }
      preview.classList.remove("hidden");
      placeholder?.classList.add("hidden");
    } else if (preview) {
      preview.src = "";
      delete preview.dataset.currentUrl;
      preview.classList.add("hidden");
      placeholder?.classList.remove("hidden");
    }
  }

  private handleImageUpload(input: HTMLInputElement, questionId: string): void {
    const file = input.files?.[0];
    if (!file) return;

    const url = "/rails/active_storage/direct_uploads";
    const upload = new DirectUpload(file, url);
    const card = input.closest(".image-upload-area");
    const loading = card?.querySelector(".loading-overlay");

    loading?.classList.remove("hidden");

    upload.create((error: Error | null, blob: { signed_id: string }) => {
      loading?.classList.add("hidden");
      if (error) {
        this.showToast("Image upload failed", "error");
      } else {
        const imageUrl = URL.createObjectURL(file);
        updateQuestion(this.testId, questionId, {
          image: blob.signed_id,
          image_url: imageUrl,
        });
      }
    });
  }

  private bindEvents(): void {
    this.titleInput?.addEventListener("input", () => {
      if (this.titleInput) {
        updateTitle(this.testId, this.titleInput.value);
      }
    });

    this.difficultySelect?.addEventListener("change", () => {
      if (this.difficultySelect) {
        updateDifficulty(this.testId, this.difficultySelect.value);
      }
    });

    this.descriptionTextarea?.addEventListener("input", () => {
      if (this.descriptionTextarea) {
        updateDescription(this.testId, this.descriptionTextarea.value);
      }
    });

    this.timeLimitInput?.addEventListener("input", () => {
      if (this.timeLimitInput) {
        const val = parseInt(this.timeLimitInput.value) || 0;
        updateTimeLimit(this.testId, val * 60);
      }
    });

    this.shuffleAnswersToggle?.addEventListener("click", () => {
      const current = this.shuffleAnswersToggle?.dataset.shuffle === "true";
      const newValue = !current;
      updateShuffleAnswers(this.testId, newValue);
      this.updateShuffleAnswersToggle(newValue);
    });

    this.rootElement.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      if (target.closest('[data-action="test-editor-layout#showCanvas"]')) {
        this.showEditorSection("canvas");
        return;
      }
      if (target.closest('[data-action="test-editor-layout#showToolbox"]')) {
        this.showEditorSection("toolbox");
        return;
      }
      if (target.closest('[data-action="test-editor-layout#showProperties"]')) {
        this.showEditorSection("properties");
        return;
      }

      const uploadArea = target.closest(".image-upload-area");
      if (uploadArea && !target.classList.contains("question-image-input")) {
        const input = queryOptional<HTMLInputElement>(
          ".question-image-input",
          uploadArea,
        );
        input?.click();
        return;
      }

      const questionCard = target.closest<HTMLElement>(".question-card");
      const questionId = questionCard?.dataset?.questionId;

      const clickedAddQ = target.closest("#add-question-btn");
      if (clickedAddQ) {
        addQuestion(this.testId);
        return;
      }

      if (target.closest("#publish-btn")) {
        this.publish();
        return;
      }

      if (target.closest("#save-now-btn")) {
        this.saveNow();
        return;
      }

      if (!questionId) return;

      if (target.closest(".remove-question-btn")) {
        updateQuestion(this.testId, questionId, { _destroy: true });
      } else if (target.closest(".add-option-btn")) {
        addAnswer(this.testId, questionId);
      } else if (target.closest(".answer-correct-toggle")) {
        const row = target.closest<HTMLElement>(".answer-option-row");
        const answerId = row?.dataset?.answerId;
        if (answerId) {
          const toggle = target.closest(".answer-correct-toggle");
          if (toggle) {
            const isCorrect = toggle.classList.contains("is-correct");
            updateAnswer(this.testId, questionId, answerId, {
              is_correct: !isCorrect,
            });
          }
        }
      } else if (target.closest(".remove-answer-btn")) {
        const row = target.closest<HTMLElement>(".answer-option-row");
        const answerId = row?.dataset?.answerId;
        if (answerId) {
          removeAnswer(this.testId, questionId, answerId);
        }
      }
    });

    this.rootElement.addEventListener("input", (e) => {
      const target = e.target as HTMLElement;
      const questionCard = target.closest<HTMLElement>(".question-card");
      const questionId = questionCard?.dataset?.questionId;
      if (!questionId) return;

      if (target.classList.contains("question-content")) {
        const textarea = target as HTMLTextAreaElement;
        updateQuestion(this.testId, questionId, { content: textarea.value });
      } else if (target.classList.contains("answer-content")) {
        const input = target as HTMLInputElement;
        const row = target.closest<HTMLElement>(".answer-option-row");
        const answerId = row?.dataset?.answerId;
        if (answerId) {
          updateAnswer(this.testId, questionId, answerId, {
            content: input.value,
          });
        }
      }
    });

    this.rootElement.addEventListener("change", (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("question-image-input")) {
        const questionCard = target.closest<HTMLElement>(".question-card");
        const questionId = questionCard?.dataset?.questionId;
        if (questionId) {
          this.handleImageUpload(target as HTMLInputElement, questionId);
        }
      }
    });
  }

  private renderTitle(title: string): void {
    if (this.titleInput && this.titleInput.value !== title) {
      this.titleInput.value = title;
    }
  }

  private renderQuestions(questions: Question[]): void {
    if (!this.questionsContainer) return;

    const activeQuestions = questions.filter((q) => !q._destroy);

    const existingCards = new Map<string, HTMLElement>();
    this.questionsContainer
      .querySelectorAll<HTMLElement>(".question-card")
      .forEach((card) => {
        const id = card.dataset.questionId;
        if (id) existingCards.set(id, card);
      });

    const container = this.questionsContainer;

    activeQuestions.forEach((q, index) => {
      let card = existingCards.get(q.uuid);

      if (card) {
        const label = card.querySelector(".question-number-label");
        if (label) {
          label.textContent = `Question ${index + 1}`;
        }
        const textarea = queryOptional<HTMLTextAreaElement>(
          ".question-content",
          card,
        );
        if (textarea && textarea.value !== q.content) {
          textarea.value = q.content ?? "";
        }

        this.renderAnswers(card, q);
        this.renderQuestionImage(card, q);

        if (card.parentElement !== container) {
          container.appendChild(card);
        }
      } else {
        card = this.createQuestionCard(q, index);
        container.appendChild(card);
      }
    });

    existingCards.forEach((card, uuid) => {
      if (!activeQuestions.find((q) => q.uuid === uuid)) {
        card.remove();
      }
    });

    this.initSortable();
  }

  private renderAnswers(questionCard: HTMLElement, q: Question): void {
    const container = questionCard.querySelector(".answers-container");
    if (!container) return;

    const activeAnswers = (q.answer_options || []).filter((a) => !a._destroy);
    const existingRows = new Map<string, HTMLElement>();
    container
      .querySelectorAll<HTMLElement>(".answer-option-row")
      .forEach((row) => {
        const id = row.dataset.answerId;
        if (id) existingRows.set(id, row);
      });

    activeAnswers.forEach((a) => {
      let row = existingRows.get(a.uuid);
      if (row) {
        const input = queryOptional<HTMLInputElement>(".answer-content", row);
        if (input && input.value !== a.content) {
          input.value = a.content;
        }
        const toggle = queryOptional<HTMLElement>(
          ".answer-correct-toggle",
          row,
        );
        if (toggle) {
          this.updateToggleVisuals(toggle, !!a.is_correct);
        }
      } else {
        row = this.createAnswerRow(a);
        container.appendChild(row);
      }
    });

    existingRows.forEach((row, uuid) => {
      if (!activeAnswers.find((a) => a.uuid === uuid)) {
        row.remove();
      }
    });
  }

  private updateToggleVisuals(toggle: HTMLElement, isCorrect: boolean): void {
    toggle.classList.toggle("is-correct", isCorrect);
    if (isCorrect) {
      toggle.classList.remove(
        "text-transparent",
        "border-slate-600",
        "hover:border-purple-500",
        "hover:text-purple-500",
      );
      toggle.classList.add("text-emerald-500", "border-emerald-500");
    } else {
      toggle.classList.add(
        "text-transparent",
        "border-slate-600",
        "hover:border-purple-500",
        "hover:text-purple-500",
      );
      toggle.classList.remove("text-emerald-500", "border-emerald-500");
    }
  }

  private createAnswerRow(a: AnswerOption): HTMLElement {
    const template = queryOptional<HTMLTemplateElement>("#answer-template");
    if (!template)
      throw new Error("Required element not found: #answer-template");
    const clone = template.content.cloneNode(true) as DocumentFragment;
    const row = clone.querySelector<HTMLElement>(".answer-option-row");
    if (!row) throw new Error("Required element not found: .answer-option-row");

    row.dataset.answerId = a.uuid;

    const input = queryOptional<HTMLInputElement>(".answer-content", row);
    if (input) input.value = a.content ?? "";

    const toggle = queryOptional<HTMLElement>(".answer-correct-toggle", row);
    if (toggle) {
      this.updateToggleVisuals(toggle, !!a.is_correct);
    }

    return row;
  }

  private createQuestionCard(q: Question, index: number): HTMLElement {
    const template = queryOptional<HTMLTemplateElement>("#question-template");
    if (!template)
      throw new Error("Required element not found: #question-template");
    const clone = template.content.cloneNode(true) as DocumentFragment;
    const card = clone.querySelector<HTMLElement>(".question-card");
    if (!card) throw new Error("Required element not found: .question-card");

    card.dataset.questionId = q.uuid;

    const label = card.querySelector(".question-number-label");
    if (label) label.textContent = `Question ${index + 1}`;

    const textarea = queryOptional<HTMLTextAreaElement>(
      ".question-content",
      card,
    );
    if (textarea) textarea.value = q.content ?? "";

    this.renderAnswers(card, q);
    this.renderQuestionImage(card, q);

    return card;
  }

  private initSortable(): void {
    if (this.sortable) {
      this.sortable.destroy();
    }

    if (this.questionsContainer) {
      this.sortable = new Sortable(this.questionsContainer, {
        handle: ".drag-handle",
        animation: 150,
        ghostClass: "bg-slate-800",
        onEnd: () => {
          const ids = Array.from(this.questionsContainer!.children).map(
            (el) => {
              const qId = (el as HTMLElement).dataset.questionId;
              if (!qId)
                throw new Error("Question card missing data-question-id");
              return qId;
            },
          );
          reorderQuestions(this.testId, ids);
        },
      });
    }
  }

  async publish(): Promise<void> {
    const state = getState(this.testId);
    if (!state) return;

    if (!state.collection_id) {
      this.cascadingCollections?.showError();
      this.showToast("Please select a collection before publishing.", "error");
      return;
    }

    this.cascadingCollections?.hideError();

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    try {
      const response = await fetch(`/studio/tests/${this.testId}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          test: {
            ...this.formatForServer(state),
            status: "published",
          },
        }),
      });

      if (response.ok) {
        this.showToast("Published successfully!", "success");
        window.location.href = "/studio";
      } else {
        const errors = await response.json();
        this.showToast(errors.errors ?? "Failed to publish", "error");
      }
    } catch {
      this.showToast("Network error", "error");
    }
  }

  async saveNow(): Promise<void> {
    const state = getState(this.testId);
    if (!state) return;

    const data = this.formatForServer(state);
    if (!data) return;

    this.updateSaveStatus("saving");
    await this.autosave.saveNow({
      testId: this.testId,
      data: data,
      onSuccess: (serverUpdatedAt) => {
        this.serverUpdatedAt = serverUpdatedAt;
        this.updateSaveStatus("saved");
        this.showToast("Saved!", "success");
      },
      onError: (error) => {
        if (error === 404) {
          this.updateSaveStatus("offline");
          this.showToast(
            "Some questions were deleted - cleaning up...",
            "info",
          );
          this.handle404AndRefresh();
        } else {
          this.updateSaveStatus("offline");
          this.showToast("Save failed - changes saved locally", "error");
        }
      },
    });
  }

  private showToast(message: string, type: string = "info"): void {
    const toast = document.createElement("div");
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  private showEditorSection(
    section: "canvas" | "toolbox" | "properties",
  ): void {
    const sections = {
      canvas: { el: this.canvasElement, btn: this.canvasBtn },
      toolbox: { el: this.toolboxElement, btn: this.toolboxBtn },
      properties: { el: this.propertiesElement, btn: this.propertiesBtn },
    };

    Object.entries(sections).forEach(([key, value]) => {
      const active = key === section;
      if (value.el) {
        if (active) {
          value.el.classList.remove("hidden");
          if (key !== "canvas") value.el.classList.add("flex");
        } else {
          value.el.classList.add("hidden");
          if (key !== "canvas") value.el.classList.remove("flex");
        }
      }
      if (value.btn) {
        if (active) {
          value.btn.classList.add("bg-slate-700", "text-white");
        } else {
          value.btn.classList.remove("bg-slate-700", "text-white");
        }
      }
    });
  }
}
